import React, { useCallback, useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { useNavigate } from "react-router-dom";
import {
    Activity, RefreshCw, TriangleAlert, ShieldCheck, HeartPulse,
    Users, Cpu, CalendarClock, Inbox, Radio, Wrench, Info, PlayCircle,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import useModules from "../hooks/useModules";
import EntityLink from "../components/EntityLink";
import DateInput from "../components/DateInput";
import ModuleIdChip from "../components/ModuleIdChip";

const POLL_MS = 5 * 60 * 1000; // 5 minutes silent refresh

function relTime(iso) {
    if (!iso) return "never";
    try {
        const then = new Date(iso).getTime();
        const now = Date.now();
        const s = Math.max(0, Math.round((now - then) / 1000));
        if (s < 60) return `${s}s ago`;
        const m = Math.round(s / 60);
        if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
        const h = Math.round(m / 60);
        if (h < 48) return `${h} hour${h === 1 ? "" : "s"} ago`;
        const d = Math.round(h / 24);
        return `${d} day${d === 1 ? "" : "s"} ago`;
    } catch {
        return "n/a";
    }
}

const SEVERITY = {
    critical: { label: "CRITICAL", cls: "bg-[#DC2626] text-white" },
    warning: { label: "WARNING", cls: "bg-[#D97706] text-white" },
};

/**
 * Turn the /health JSON into a normalised list of issue rows.
 * Each row: {id, severity, name, count, fix_label, fix_target}
 */
function buildIssueRows(h) {
    const rows = [];
    // Prefix mismatch → warning
    const pm = h?.biometric_id_prefix_mismatches?.count || 0;
    if (pm > 0) {
        rows.push({
            id: "prefix-mismatches",
            severity: "warning",
            name: "Biometric ID prefix mismatches",
            count: pm,
            fix_label: "Realign IDs",
            fix_target: { type: "navigate", to: "/admin/settings" },
        });
    }
    // Missing biometric IDs → warning
    const missing = h?.employees_missing_biometric_id?.count || 0;
    if (missing > 0) {
        rows.push({
            id: "missing-biometric-id",
            severity: "warning",
            name: `Employees missing biometric_user_id`,
            count: missing,
            fix_label: "Open Staff",
            fix_target: { type: "navigate", to: "/" },
        });
    }
    // Offline devices → critical (one row per device so it's actionable)
    (h?.devices || []).forEach((d) => {
        if (d.status === "offline") {
            const mins = d.minutes_since_last_seen;
            const detail = d.last_seen
                ? `${d.device_name} not seen in ${mins ?? "?"} min`
                : `${d.device_name} never pushed events`;
            rows.push({
                id: `device-offline-${d.id}`,
                severity: "critical",
                name: detail,
                count: 1,
                fix_label: "Check device",
                fix_target: { type: "navigate", to: "/" },
            });
        }
    });
    // Pending exceptions → critical
    const pex = h?.pending_exceptions_count || 0;
    if (pex > 0) {
        rows.push({
            id: "pending-exceptions",
            severity: "critical",
            name: "Pending attendance exceptions awaiting review",
            count: pex,
            fix_label: "Open Exceptions",
            fix_target: { type: "navigate", to: "/" },
        });
    }
    // Any other known_500_risks — bring them in as warnings (already covered above by name in most cases)
    (h?.known_500_risks || []).forEach((r, i) => {
        // De-dupe: skip if already surfaced by the specific rows above.
        const already = rows.some((x) => x.name && x.name.toLowerCase().includes((r.collection || "").toLowerCase()));
        if (already) return;
        rows.push({
            id: `risk-${i}`,
            severity: "warning",
            name: `${r.collection}: ${r.issue}`,
            count: r.count,
            fix_label: null,
            fix_target: null,
        });
    });
    return rows;
}

export default function SystemHealth() {
    const navigate = useNavigate();
    const { modules } = useModules();
    const [health, setHealth] = useState(null);
    const [audit, setAudit] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [lastChecked, setLastChecked] = useState(null);

    // --- Auto-classifier backfill dialog state ---
    const todayIso = new Date().toISOString().slice(0, 10);
    const [backfillOpen, setBackfillOpen] = useState(false);
    const [backfillDate, setBackfillDate] = useState(todayIso);
    const [backfillModule, setBackfillModule] = useState(""); // "" = all
    const [backfillRunning, setBackfillRunning] = useState(false);
    const [backfillResult, setBackfillResult] = useState(null); // dialog stays open with result
    const [showAllChanges, setShowAllChanges] = useState(false);

    const load = useCallback(async (opts = {}) => {
        if (!opts.silent) setRefreshing(true);
        try {
            const [{ data: h }, { data: a }] = await Promise.all([
                api.get("/admin/health"),
                api.get("/admin/health/audit-feed?limit=20"),
            ]);
            setHealth(h);
            setAudit(a?.items || []);
            setLastChecked(new Date().toISOString());
            setError(null);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(() => load({ silent: true }), POLL_MS);
        return () => clearInterval(id);
    }, [load]);

    const manualRefresh = async () => {
        await load();
        toast.success("Health snapshot refreshed.");
    };

    const runBackfill = async () => {
        setBackfillRunning(true);
        try {
            const params = new URLSearchParams({ date: backfillDate });
            if (backfillModule) params.set("module_id", backfillModule);
            const { data } = await api.post(`/attendance/backfill-auto-classifier?${params.toString()}`);
            setBackfillResult(data);
            toast.success("Backfill complete.");
            // Refresh audit feed so the entry appears immediately.
            load({ silent: true });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBackfillRunning(false);
        }
    };

    const resetBackfillDialog = () => {
        setBackfillOpen(false);
        setBackfillResult(null);
        setShowAllChanges(false);
    };

    if (loading) {
        return (
            <AppShell title="System Health">
                <div className="text-sm text-[color:var(--asl-muted)]">Loading…</div>
            </AppShell>
        );
    }

    if (error) {
        return (
            <AppShell title="System Health">
                <div className="asl-card p-5" data-testid="system-health-error-card">
                    <div className="flex items-start gap-3">
                        <TriangleAlert className="h-5 w-5 asl-crimson-text mt-0.5" />
                        <div className="flex-1">
                            <div className="font-semibold asl-navy-text">Couldn't load system health</div>
                            <p className="text-sm text-[color:var(--asl-muted)] mt-1">{error}</p>
                            <div className="mt-3">
                                <Button variant="outline" onClick={load}>Retry</Button>
                            </div>
                        </div>
                    </div>
                </div>
            </AppShell>
        );
    }

    const overall = health?.overall_status || "healthy";
    const rows = buildIssueRows(health);
    const isHealthy = overall === "healthy";
    const isCritical = overall === "critical";
    const bigStatus = isHealthy
        ? { label: "All systems healthy", Icon: ShieldCheck, tone: "green" }
        : (isCritical
            ? { label: `${rows.length} issue${rows.length === 1 ? "" : "s"} detected`, Icon: TriangleAlert, tone: "red" }
            : { label: `${rows.length} issue${rows.length === 1 ? "" : "s"} detected`, Icon: HeartPulse, tone: "amber" });

    const toneClasses = {
        green: { bg: "bg-[#DCFCE7]", ring: "ring-[#16A34A]/40", text: "text-[#166534]" },
        amber: { bg: "bg-[#FEF3C7]", ring: "ring-[#D97706]/40", text: "text-[#92400E]" },
        red: { bg: "bg-[#FEE2E2]", ring: "ring-[#DC2626]/40", text: "text-[#991B1B]" },
    }[bigStatus.tone];

    return (
        <AppShell title="System Health">
            <section className="space-y-6" data-testid="system-health-page">
                <header className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Activity className="h-6 w-6 asl-navy-text" />
                        <div>
                            <h2 className="text-xl font-bold asl-navy-text">System Health</h2>
                            <p className="text-sm text-[color:var(--asl-muted)]">
                                Live snapshot of platform integrity. Auto-refresh every 5 minutes.
                                {lastChecked && (
                                    <> · Last checked <span className="mono">{relTime(lastChecked)}</span></>
                                )}
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        onClick={manualRefresh}
                        disabled={refreshing}
                        data-testid="system-health-refresh"
                        className="gap-2"
                    >
                        <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                        {refreshing ? "Refreshing…" : "Refresh now"}
                    </Button>
                </header>

                {/* Big status card */}
                <div
                    className={`rounded-lg ring-1 ${toneClasses.ring} ${toneClasses.bg} p-6 flex items-center gap-4`}
                    data-testid="system-health-status-card"
                >
                    <bigStatus.Icon className={`h-10 w-10 ${toneClasses.text}`} />
                    <div>
                        <div className={`text-2xl font-bold ${toneClasses.text}`}>{bigStatus.label}</div>
                        <div className={`text-xs ${toneClasses.text} opacity-80 mt-0.5`}>
                            overall_status: <span className="mono">{overall}</span>
                        </div>
                    </div>
                </div>

                {/* Metric cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    <MetricCard
                        icon={Users}
                        label="Employees"
                        value={health?.counts?.employees ?? "—"}
                        testid="system-health-metric-employees"
                    />
                    <MetricCard
                        icon={Cpu}
                        label="Devices online"
                        value={
                            <>
                                <span>{health?.devices_summary?.online ?? 0}</span>
                                <span className="text-sm text-[color:var(--asl-muted)]">
                                    {" / "}{health?.devices_summary?.total ?? 0}
                                </span>
                            </>
                        }
                        testid="system-health-metric-devices"
                    />
                    <MetricCard
                        icon={CalendarClock}
                        label="Events today"
                        value={health?.events_today_count ?? 0}
                        testid="system-health-metric-events-today"
                    />
                    <MetricCard
                        icon={Inbox}
                        label="Pending exceptions"
                        value={health?.pending_exceptions_count ?? 0}
                        testid="system-health-metric-pending-exceptions"
                    />
                    <MetricCard
                        icon={Radio}
                        label="Last device push"
                        value={
                            <span className="text-base">{relTime(health?.devices_summary?.latest_last_seen)}</span>
                        }
                        testid="system-health-metric-last-push"
                    />
                </div>

                {/* System Tools */}
                <div className="asl-card p-5" data-testid="system-health-tools-card">
                    <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 asl-navy-bg text-white rounded-md">
                            <Wrench className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-base font-bold asl-navy-text">System Tools</h3>
                            <p className="text-sm text-[color:var(--asl-muted)] mt-1">
                                One-shot maintenance actions. Every run is written to the audit log.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start justify-between gap-3 flex-wrap pt-2 border-t border-[color:var(--asl-border,#E5E7EB)]">
                        <div className="flex items-start gap-2 flex-1 min-w-[240px]">
                            <PlayCircle className="h-4 w-4 asl-navy-text mt-0.5 shrink-0" />
                            <div>
                                <div className="text-sm font-semibold asl-navy-text flex items-center gap-1.5">
                                    Run Auto-Classifier Backfill
                                    <span
                                        title="Re-runs the first-scan-of-day = Check-In rule for all auto-classified attendance events on a specific date. Useful for cleaning up rows that were classified before the current logic. Preserves manual overrides and device-explicit taps."
                                        className="cursor-help inline-flex"
                                    >
                                        <Info className="h-3.5 w-3.5 text-[color:var(--asl-muted)]" />
                                    </span>
                                </div>
                                <p className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                    Re-classify auto-tagged attendance events for a specific date.
                                    Manual overrides and device-explicit Check In/Out are preserved.
                                </p>
                            </div>
                        </div>
                        <Button
                            onClick={() => {
                                setBackfillDate(todayIso);
                                setBackfillModule("");
                                setBackfillResult(null);
                                setShowAllChanges(false);
                                setBackfillOpen(true);
                            }}
                            className="asl-crimson-bg text-white hover:opacity-90"
                            data-testid="backfill-classifier-btn"
                        >
                            Run Auto-Classifier Backfill
                        </Button>
                    </div>
                </div>

                {/* Issues */}
                {rows.length > 0 && (
                    <div className="asl-card p-5" data-testid="system-health-issues-card">
                        <h3 className="text-base font-bold asl-navy-text mb-3">Active issues</h3>
                        <div className="overflow-x-auto">
                            <table className="asl-table asl-table--zebra">
                                <thead>
                                    <tr className="text-left text-xs text-[color:var(--asl-muted)] border-b border-[color:var(--asl-border,#E5E7EB)]">
                                        <th className="py-2 px-2">Severity</th>
                                        <th className="py-2 px-2">Issue</th>
                                        <th className="py-2 px-2">Affected</th>
                                        <th className="py-2 px-2 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr
                                            key={r.id}
                                            className="border-b border-[color:var(--asl-border,#E5E7EB)] last:border-b-0"
                                            data-testid={`system-health-issue-row-${r.id}`}
                                        >
                                            <td className="py-2 px-2">
                                                <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ${SEVERITY[r.severity].cls}`}>
                                                    {SEVERITY[r.severity].label}
                                                </span>
                                            </td>
                                            <td className="py-2 px-2">{r.name}</td>
                                            <td className="py-2 px-2 mono">{r.count}</td>
                                            <td className="py-2 px-2 text-right">
                                                {r.fix_target ? (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => navigate(r.fix_target.to)}
                                                        data-testid={`system-health-fix-btn-${r.id}`}
                                                    >
                                                        {r.fix_label}
                                                    </Button>
                                                ) : (
                                                    <span className="text-xs text-[color:var(--asl-muted)] italic">
                                                        no auto-fix
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Recent activity feed */}
                <div className="asl-card p-5" data-testid="system-health-history">
                    <h3 className="text-base font-bold asl-navy-text mb-3">Recent admin activity</h3>
                    {audit.length === 0 ? (
                        <div className="text-xs text-[color:var(--asl-muted)] italic">No recent audit entries.</div>
                    ) : (
                        <ul className="text-xs space-y-2">
                            {audit.map((a) => (
                                <li key={a.id} className="flex items-start gap-3 border-b border-[color:var(--asl-border,#E5E7EB)] pb-2 last:border-b-0 last:pb-0">
                                    <span className="mono text-[color:var(--asl-muted)] shrink-0 w-40">
                                        {relTime(a.timestamp)}
                                    </span>
                                    <span className="mono font-semibold asl-navy-text w-56 shrink-0">{a.action}</span>
                                    <span className="text-[color:var(--asl-muted)]">
                                        by <span className="mono">{a.actor_email || a.actor_user_id || "system"}</span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            {/* Auto-Classifier Backfill dialog */}
            <Dialog open={backfillOpen} onOpenChange={(o) => !o && !backfillRunning && resetBackfillDialog()}>
                <DialogContent className="max-w-2xl" data-testid="backfill-classifier-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {backfillResult ? "Backfill complete" : "Run Auto-Classifier Backfill"}
                        </DialogTitle>
                    </DialogHeader>
                    {!backfillResult ? (
                        <div className="space-y-4">
                            <p className="text-sm text-[color:var(--asl-muted)]">
                                This will re-classify auto-classified events for the selected date.
                                <span className="mono"> hikvision_status</span> and
                                <span className="mono"> manual_override</span> events are preserved unchanged.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <label className="block">
                                    <span className="text-xs font-semibold asl-navy-text uppercase tracking-wide">Date</span>
                                    <DateInput
                                        value={backfillDate}
                                        onChange={setBackfillDate}
                                        className="mt-1 w-full h-9 px-3 border rounded-md text-sm mono"
                                        style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
                                        data-testid="backfill-classifier-date"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold asl-navy-text uppercase tracking-wide">Module (optional)</span>
                                    <select
                                        value={backfillModule}
                                        onChange={(e) => setBackfillModule(e.target.value)}
                                        className="mt-1 w-full h-9 px-3 border rounded-md text-sm bg-white"
                                        style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
                                        data-testid="backfill-classifier-module"
                                    >
                                        <option value="">All modules</option>
                                        {(modules || []).map((m) => (
                                            <option key={m.id} value={m.id}>
                                                {m.code} — {m.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-3">
                                <ResultStat
                                    label="Events touched"
                                    value={backfillResult.touched_events}
                                    testid="backfill-classifier-result-events"
                                />
                                <ResultStat
                                    label="Employees affected"
                                    value={backfillResult.touched_employees}
                                    testid="backfill-classifier-result-employees"
                                />
                                <ResultStat
                                    label="Days recomputed"
                                    value={backfillResult.recomputed_days}
                                    testid="backfill-classifier-result-recomputed"
                                />
                            </div>
                            {(backfillResult.changes || []).length === 0 ? (
                                <p className="text-sm text-[color:var(--asl-muted)] italic">
                                    Nothing to backfill on <span className="mono">{backfillResult.date}</span> — all
                                    auto-classified events are already aligned.
                                </p>
                            ) : (
                                <details
                                    className="border border-[color:var(--asl-border,#E5E7EB)] rounded-md"
                                    open={showAllChanges}
                                    data-testid="backfill-classifier-changes"
                                >
                                    <summary
                                        className="px-3 py-2 text-sm cursor-pointer select-none asl-navy-text font-semibold"
                                        onClick={() => setShowAllChanges((v) => !v)}
                                    >
                                        See changes ({(backfillResult.changes || []).length})
                                    </summary>
                                    <div className="max-h-64 overflow-auto border-t border-[color:var(--asl-border,#E5E7EB)]">
                                        <table className="w-full text-xs">
                                            <thead className="asl-navy-bg text-white sticky top-0">
                                                <tr>
                                                    <th className="text-left px-3 py-2">Employee</th>
                                                    <th className="text-left px-3 py-2">At</th>
                                                    <th className="text-left px-3 py-2">From</th>
                                                    <th className="text-left px-3 py-2">To</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {backfillResult.changes.slice(0, 20).map((c, i) => {
                                                    const display = c.employee_name || c.employee_display || "Unknown employee";
                                                    const mid = c.employee_module_employee_id || "";
                                                    const mc = (c.module_code || "").toLowerCase();
                                                    const to = mc && c.employee_id ? `/m/${mc}/staff/${c.employee_id}` : null;
                                                    return (
                                                        <tr key={c.event_id || i} className="border-t border-[color:var(--asl-border,#E5E7EB)]">
                                                            <td className="px-3 py-1.5">
                                                                <span className="inline-flex items-center gap-2">
                                                                    {to ? (
                                                                        <EntityLink
                                                                            to={to}
                                                                            onNav={() => setBackfillResult(null)}
                                                                            testid={`sh-backfill-emp-${c.employee_id}`}
                                                                        >
                                                                            {display}
                                                                        </EntityLink>
                                                                    ) : display}
                                                                    <ModuleIdChip value={mid} testid={`sh-backfill-mid-${c.employee_id || i}`} />
                                                                </span>
                                                            </td>
                                                            <td className="px-3 py-1.5 mono">{c.at || "—"}</td>
                                                            <td className="px-3 py-1.5 mono asl-crimson-text">{c.from || "—"}</td>
                                                            <td className="px-3 py-1.5 mono font-semibold asl-navy-text">{c.to || "—"}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        {backfillResult.changes.length > 20 && (
                                            <div className="px-3 py-2 text-xs text-[color:var(--asl-muted)] italic border-t border-[color:var(--asl-border,#E5E7EB)]">
                                                Showing first 20 of {backfillResult.changes.length} changes.
                                            </div>
                                        )}
                                    </div>
                                </details>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        {!backfillResult ? (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={resetBackfillDialog}
                                    disabled={backfillRunning}
                                    data-testid="backfill-classifier-cancel"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    className="asl-crimson-bg text-white"
                                    onClick={runBackfill}
                                    disabled={backfillRunning || !backfillDate}
                                    data-testid="backfill-classifier-run"
                                >
                                    {backfillRunning ? "Running…" : "Run Backfill"}
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={resetBackfillDialog}
                                className="asl-navy-bg text-white"
                                data-testid="backfill-classifier-close"
                            >
                                Close
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}

function ResultStat({ label, value, testid }) {
    return (
        <div className="asl-card p-3 text-center" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wide text-[color:var(--asl-muted)]">{label}</div>
            <div className="text-2xl font-bold asl-navy-text mt-1 mono">{value ?? 0}</div>
        </div>
    );
}

function MetricCard({ icon: Icon, label, value, testid }) {
    return (
        <div className="asl-card p-4" data-testid={testid}>
            <div className="flex items-center gap-2 text-xs text-[color:var(--asl-muted)] uppercase tracking-wide">
                <Icon className="h-3.5 w-3.5" />
                <span>{label}</span>
            </div>
            <div className="text-2xl font-bold asl-navy-text mt-1">{value}</div>
        </div>
    );
}
